import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useContactModal } from "../context/ContactModalContext";
import ImageTile from "../components/ImageTile";
import ProductCard from "../components/ProductCard";
import AvailabilityChart from "../components/AvailabilityChart";
import { findProduct } from "../data/products";
import { getProductDetails } from "../data/productDetails";
import { heroImages, aboutImages, productGalleries } from "../data/images";

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "specs", label: "Specifications" },
  { key: "packaging", label: "Packaging & Storage" },
  { key: "shipping", label: "Shipping & Logistics" },
  { key: "quality", label: "Quality Standards" },
];

export default function ProductDetailPage() {
  const { category, slug } = useParams();
  const { openModal } = useContactModal();
  const [activeTab, setActiveTab] = useState("overview");

  const found = findProduct(category, slug);
  if (!found) return <Navigate to={`/products/${category || ""}`} replace />;

  const { category: cat, item } = found;
  const related = cat.items.filter((i) => i.slug !== item.slug).slice(0, 4);
  const details = getProductDetails(item.slug);
  const isAmbient = /ambient/i.test(details.storage[0]?.[1] || "");

  const scrollToTab = (tabKey) => {
    setActiveTab(tabKey);
    const el = document.getElementById("product-tabs");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      {/* ── PAGE TITLE BANNER (right under navbar) ── */}
      <section className="relative w-full mt-16 md:mt-20 overflow-hidden">
        <div
          className="relative w-full py-20 md:py-28 overflow-hidden"
          style={{
            background:
              "radial-gradient(ellipse at 18% 25%, rgba(107,142,35,0.22), transparent 55%)," +
              "radial-gradient(ellipse at 82% 75%, rgba(45,212,191,0.18), transparent 55%)," +
              "linear-gradient(135deg, #E8EFD9 0%, #DCE7C8 35%, #D4E2BD 70%, #C9DBB0 100%)",
          }}
        >
          {/* Subtle dot grid texture */}
          <div className="absolute inset-0 opacity-[0.10] pointer-events-none"
            style={{ backgroundImage: "radial-gradient(rgba(61,61,51,0.5) 1px, transparent 1px)", backgroundSize: "20px 20px" }} />

          {/* Soft glow blobs */}
          <div className="absolute -top-20 -left-10 w-96 h-96 rounded-full blur-3xl opacity-40 pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(134,239,172,0.45), transparent 70%)" }} />
          <div className="absolute -bottom-24 right-0 w-md h-112 rounded-full blur-3xl opacity-30 pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(107,142,35,0.40), transparent 70%)" }} />

          <div className="relative w-full px-4 sm:px-6 lg:px-10 max-w-400 mx-auto text-center fade-up">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/60 border border-emerald-700/15 backdrop-blur-sm mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
              <span className="text-[11px] uppercase tracking-[0.22em] text-emerald-800 font-medium">{cat.label}</span>
            </div>
            <h1 className="font-display text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-semibold tracking-tight text-stone-900 leading-[1.02] uppercase">
              {item.name}
            </h1>
            <nav className="mt-6 flex items-center justify-center gap-2 text-sm text-stone-600">
              <Link to="/" className="hover:text-emerald-700 transition">Home</Link>
              <span className="text-stone-400">/</span>
              <Link to="/products" className="hover:text-emerald-700 transition">Products</Link>
              <span className="text-stone-400">/</span>
              <Link to={`/products/${cat.key}`} className="hover:text-emerald-700 transition">{cat.label}</Link>
              <span className="text-stone-400">/</span>
              <span className="text-stone-900 font-medium">{item.name}</span>
            </nav>
          </div>
        </div>
      </section>

      {/* ── HERO: Image + product info (no form sidebar) ── */}
      <section className="relative w-full overflow-hidden">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-400 mx-auto py-12 md:py-16">
          <div className="grid lg:grid-cols-12 gap-10 items-center">

            {/* IMAGE GALLERY */}
            <div className="lg:col-span-5 fade-up">
              <div className="relative rounded-3xl overflow-hidden border border-stone-900/10 shadow-xl shadow-stone-900/5">
                <ImageTile src={item.img} alt={item.name} emoji={item.emoji}
                  className="aspect-4/5 md:aspect-4/3"
                  imgClassName="hover:scale-105 transition-transform duration-700"
                  overlayClassName="bg-gradient-to-t from-black/30 via-transparent to-transparent">
                  <div className={`absolute top-4 left-4 rounded-full px-3 py-1.5 text-[10px] uppercase tracking-widest text-amber-950 font-medium bg-linear-to-br ${cat.accent}`}>
                    {cat.label}
                  </div>
                </ImageTile>
              </div>

              {/* Thumbnail strip — main shot, two product-specific detail
                  photos (verified per slug), main shot. */}
              <div className="mt-3 grid grid-cols-4 gap-3">
                {(() => {
                  const [g1, g2, g3] = productGalleries[item.slug] || [];
                  return [item.img, g1 || item.img, g2 || item.img, g3 || item.img];
                })().map((img, i) => (
                  <button key={i}
                    className={`relative aspect-4/3 rounded-xl overflow-hidden border transition ${i === 0 ? "border-emerald-600 ring-2 ring-emerald-600/20" : "border-stone-900/10 hover:border-stone-900/30"}`}>
                    <ImageTile src={img} alt="" emoji={item.emoji}
                      className="w-full h-full"
                      overlayClassName="bg-black/0" />
                  </button>
                ))}
              </div>
            </div>

            {/* PRODUCT INFO */}
            <div className="lg:col-span-7 fade-up" style={{ animationDelay: "0.1s" }}>
              <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-medium mb-2">
                {cat.label} · Export Grade
              </div>
              <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold leading-[1.05] text-stone-900">
                {item.name}
              </h2>

              {item.tagline && (
                <p className="mt-4 text-base md:text-lg text-stone-700 leading-relaxed">{item.tagline}</p>
              )}

              {/* Quick attributes — pill row */}
              <div className="mt-6 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 text-xs text-stone-700 px-3 py-1.5 rounded-full bg-stone-900/5 border border-stone-900/10">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
                  {item.season}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs text-stone-700 px-3 py-1.5 rounded-full bg-stone-900/5 border border-stone-900/10">
                  Origin · India
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs text-stone-700 px-3 py-1.5 rounded-full bg-stone-900/5 border border-stone-900/10">
                  FSSAI · APEDA
                </span>
              </div>

              {/* Spec preview as horizontal definition list */}
              {item.specs && (
                <dl className="mt-7 divide-y divide-stone-900/8 border-y border-stone-900/8">
                  {item.specs.slice(0, 4).map((s) => (
                    <div key={s.label} className="flex py-3 gap-4">
                      <dt className="w-36 shrink-0 text-[11px] uppercase tracking-widest text-stone-400 pt-0.5">{s.label}</dt>
                      <dd className="flex-1 text-sm text-stone-800 font-medium">{s.value}</dd>
                    </div>
                  ))}
                </dl>
              )}

              {/* CTA buttons */}
              <div className="mt-8 flex flex-wrap gap-3">
                <button onClick={openModal}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-sm font-medium text-white bg-stone-900 hover:bg-stone-800 transition">
                  Request Quote
                  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                </button>
                <a href="tel:+919876543210"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-sm font-medium text-stone-800 border border-stone-900/15 hover:border-stone-900/30 hover:bg-stone-900/5 transition">
                  <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h2.5a1 1 0 01.95.68l1.5 4.5a1 1 0 01-.5 1.21l-2 1A11 11 0 0014 17l1-2a1 1 0 011.21-.5l4.5 1.5a1 1 0 01.68.95V19a2 2 0 01-2 2A18 18 0 013 5z" />
                  </svg>
                  Call us
                </a>
                <button onClick={() => scrollToTab("specs")}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-sm font-medium text-stone-700 hover:text-emerald-700 transition">
                  View specifications
                  <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TAB NAVIGATION (sticky) ── */}
      <section id="product-tabs" className="sticky top-16 md:top-20 z-30 bg-[#F5F1E8]/95 backdrop-blur-xl border-b border-stone-900/10">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-400 mx-auto">
          <div role="tablist" className="flex gap-1 sm:gap-2 overflow-x-auto scrollbar-hide -mb-px">
            {TABS.map((t) => {
              const isActive = activeTab === t.key;
              return (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveTab(t.key)}
                  className={`relative px-4 md:px-5 py-4 text-sm whitespace-nowrap transition font-medium border-b-2
                    ${isActive
                      ? "text-emerald-800 border-emerald-700"
                      : "text-stone-500 border-transparent hover:text-stone-900 hover:border-stone-300"}`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── TAB CONTENT ── */}
      <section className="relative py-12 md:py-16 bg-white/40">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-400 mx-auto">

          {/* OVERVIEW TAB */}
          {activeTab === "overview" && (
            <div className="grid lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-7">
                <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-medium mb-3">About this product</div>
                <h2 className="font-display text-3xl md:text-4xl font-semibold text-stone-900 leading-tight">
                  Premium <span className="text-gradient">{item.name.toLowerCase()}</span>, built for export.
                </h2>
                {item.description && (
                  <p className="mt-5 text-stone-700 leading-relaxed text-base">{item.description}</p>
                )}

                {/* Inline highlights as checked list */}
                {item.highlights && item.highlights.length > 0 && (
                  <ul className="mt-7 space-y-2.5">
                    {item.highlights.map((h, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-stone-700 leading-relaxed">
                        <svg viewBox="0 0 24 24" className="shrink-0 w-5 h-5 text-emerald-700 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                        {h}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Varieties as side panel */}
              {item.varieties && item.varieties.length > 0 && (
                <div className="lg:col-span-5">
                  <div className="rounded-3xl border border-stone-900/10 bg-white overflow-hidden">
                    <div className="px-6 py-4 border-b border-stone-900/8 bg-stone-50/50">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-stone-400">Varieties Supplied</div>
                      <div className="font-display text-lg text-stone-900 mt-0.5">Choose your grade</div>
                    </div>
                    <ul className="divide-y divide-stone-900/8">
                      {item.varieties.map((v, i) => (
                        <li key={v.name} className="px-6 py-4 flex items-start gap-4 hover:bg-stone-50/40 transition">
                          <div className={`shrink-0 w-9 h-9 rounded-lg bg-linear-to-br ${cat.accent} flex items-center justify-center text-amber-950 font-semibold text-xs`}>
                            {String(i + 1).padStart(2, "0")}
                          </div>
                          <div>
                            <div className="font-display text-base font-medium text-stone-900">{v.name}</div>
                            <p className="text-sm text-stone-600 mt-1 leading-relaxed">{v.note}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* SPECIFICATIONS TAB — striped data table */}
          {activeTab === "specs" && (
            <div className="grid lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-7">
                <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-medium mb-3">Technical sheet</div>
                <h2 className="font-display text-3xl md:text-4xl font-semibold text-stone-900 leading-tight">
                  Product <span className="text-gradient">specifications.</span>
                </h2>
                <p className="mt-3 text-stone-600 max-w-xl">All consignments are graded, inspected and documented to the values listed below. Custom specs available on request.</p>

                {item.specs && (
                  <div className="mt-7 rounded-2xl overflow-hidden border border-stone-900/10">
                    <table className="w-full text-sm">
                      <tbody>
                        {item.specs.map((s, i) => (
                          <tr key={s.label} className={i % 2 === 0 ? "bg-white" : "bg-stone-50/50"}>
                            <td className="px-5 py-4 text-stone-500 text-xs uppercase tracking-widest font-medium w-1/3 align-top">{s.label}</td>
                            <td className="px-5 py-4 text-stone-800 font-medium">{s.value}</td>
                          </tr>
                        ))}
                        <tr className="bg-white">
                          <td className="px-5 py-4 text-stone-500 text-xs uppercase tracking-widest font-medium align-top">Grade</td>
                          <td className="px-5 py-4 text-stone-800 font-medium">Class A · Class B · Custom</td>
                        </tr>
                        <tr className="bg-stone-50/50">
                          <td className="px-5 py-4 text-stone-500 text-xs uppercase tracking-widest font-medium align-top">Pesticide Residue</td>
                          <td className="px-5 py-4 text-stone-800 font-medium">EU MRL compliant · 3rd-party tested</td>
                        </tr>
                        <tr className="bg-white">
                          <td className="px-5 py-4 text-stone-500 text-xs uppercase tracking-widest font-medium align-top">Documentation</td>
                          <td className="px-5 py-4 text-stone-800 font-medium">Phyto · COO · Invoice · Packing list · BL</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Availability sidebar */}
              {item.availability && (
                <div className="lg:col-span-5">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-medium mb-3">Seasonal calendar</div>
                  <h3 className="font-display text-2xl md:text-3xl font-semibold text-stone-900 leading-tight mb-5">
                    When you can <span className="text-gradient">source it.</span>
                  </h3>
                  <div className="rounded-2xl border border-stone-900/10 bg-white p-6">
                    <AvailabilityChart
                      months={item.availability}
                      byVariety={item.availabilityByVariety}
                      accent={cat.accent}
                    />
                    <div className="mt-5 pt-4 border-t border-stone-900/8 flex items-center gap-4 text-[11px] text-stone-500">
                      <div className="flex items-center gap-2">
                        <span className={`w-3 h-3 rounded bg-linear-to-br ${cat.accent}`} />
                        Available
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded bg-stone-200" />
                        Off-season
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* PACKAGING TAB — alternating image+text rows */}
          {activeTab === "packaging" && (
            <div className="space-y-10">
              <div className="grid lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-5">
                  <div className="rounded-3xl overflow-hidden border border-stone-900/10 aspect-4/3">
                    <ImageTile src={heroImages.harvestHands} alt="" emoji="📦"
                      className="w-full h-full" overlayClassName="bg-black/10" />
                  </div>
                </div>
                <div className="lg:col-span-7">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-medium mb-3">Packaging Options</div>
                  <h3 className="font-display text-3xl md:text-4xl font-semibold text-stone-900 leading-tight">
                    Packed for the <span className="text-gradient">channel you serve.</span>
                  </h3>
                  <p className="mt-4 text-stone-600 leading-relaxed">From retail-ready private label to bulk pallets for industrial buyers — we adapt the packing format to the destination market and channel.</p>
                  <div className="mt-6 grid sm:grid-cols-2 gap-3">
                    {details.packaging.map((p, i) => (
                      <div key={i} className="rounded-xl border border-stone-900/10 bg-white p-4">
                        <div className="font-display text-base font-medium text-stone-900">{p.title}</div>
                        <p className="text-xs text-stone-600 mt-1.5 leading-relaxed">{p.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Storage row — reversed */}
              <div className="grid lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-7 lg:order-1 order-2">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-medium mb-3">Storage & Handling</div>
                  <h3 className="font-display text-3xl md:text-4xl font-semibold text-stone-900 leading-tight">
                    {isAmbient ? (
                      <>Sealed, sterilized, <span className="text-gradient">end to end.</span></>
                    ) : (
                      <>Cold chain <span className="text-gradient">never breaks.</span></>
                    )}
                  </h3>
                  <p className="mt-4 text-stone-600 leading-relaxed">Conditions held tight from pack-house to discharge port — the values below are calibrated for {item.name.toLowerCase()} and adjusted per consignment as required.</p>

                  <table className="mt-6 w-full text-sm rounded-xl overflow-hidden border border-stone-900/10">
                    <tbody>
                      {details.storage.map(([k, v], i) => (
                        <tr key={k} className={i % 2 === 0 ? "bg-white" : "bg-stone-50/60"}>
                          <td className="px-4 py-3 text-stone-500 text-xs uppercase tracking-widest w-1/2">{k}</td>
                          <td className="px-4 py-3 text-stone-800 font-medium">{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="lg:col-span-5 lg:order-2 order-1">
                  <div className="rounded-3xl overflow-hidden border border-stone-900/10 aspect-4/3">
                    <ImageTile src={aboutImages.warehouse} alt="" emoji="❄️"
                      className="w-full h-full" overlayClassName="bg-black/10" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SHIPPING TAB — process timeline + commercial terms */}
          {activeTab === "shipping" && (
            <div className="grid lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-7">
                <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-medium mb-3">From Inquiry to Port</div>
                <h2 className="font-display text-3xl md:text-4xl font-semibold text-stone-900 leading-tight">
                  How we <span className="text-gradient">deliver.</span>
                </h2>
                <p className="mt-3 text-stone-600 max-w-xl">A predictable five-step pipeline from your enquiry to the discharge port.</p>

                {/* Vertical numbered timeline */}
                <ol className="mt-8 relative border-l-2 border-emerald-600/30 ml-4 space-y-7">
                  {[
                    { step: "01", title: "Inquiry received", text: "Share product, volume, destination port and timeline." },
                    { step: "02", title: "Quotation issued", text: "Tailored offer within 1 business day with full Incoterm." },
                    { step: "03", title: "Sourcing & lot tag", text: "Procurement from cluster farms, lot-tagged for traceability." },
                    { step: "04", title: "Pack-house & inspection", text: "Pack-house grading, pre-cooling, third-party inspection." },
                    { step: "05", title: "Container dispatch", text: "Reefer booked, documents released, real-time tracking." },
                  ].map((s) => (
                    <li key={s.step} className="relative pl-8">
                      <div className="absolute left-[-1.05rem] top-0 w-9 h-9 rounded-full bg-white border-2 border-emerald-600 flex items-center justify-center text-emerald-700 text-xs font-semibold">
                        {s.step}
                      </div>
                      <div className="font-display text-lg font-medium text-stone-900">{s.title}</div>
                      <p className="text-sm text-stone-600 mt-1 leading-relaxed">{s.text}</p>
                    </li>
                  ))}
                </ol>
              </div>

              {/* Commercial terms */}
              <div className="lg:col-span-5">
                <div className="rounded-3xl bg-white border border-stone-900/10 overflow-hidden">
                  <div className="px-6 py-4 border-b border-stone-900/8 bg-stone-50/50">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-stone-400">Commercial Terms</div>
                    <div className="font-display text-lg text-stone-900 mt-0.5">At a glance</div>
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      {details.commercial.map(([k, v], i) => (
                        <tr key={k} className={i % 2 === 0 ? "bg-white" : "bg-stone-50/50"}>
                          <td className="px-5 py-3 text-stone-500 text-xs uppercase tracking-widest w-1/2">{k}</td>
                          <td className="px-5 py-3 text-stone-800 font-medium">{v}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* QUALITY TAB — certifications & quality assurance */}
          {activeTab === "quality" && (
            <div className="grid lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-12">
                <div className="text-center max-w-2xl mx-auto mb-10">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-medium mb-3">Quality Assurance</div>
                  <h2 className="font-display text-3xl md:text-4xl font-semibold text-stone-900 leading-tight">
                    Certified, traced, <span className="text-gradient">trusted.</span>
                  </h2>
                  <p className="mt-3 text-stone-600">Every consignment is documented end to end — from the farm of origin to the container seal.</p>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {[
                    { code: "FSSAI", name: "Food Safety Authority", desc: "License #100xxxxxxx — issued by Food Safety and Standards Authority of India." },
                    { code: "APEDA", name: "Export Authority", desc: "Registered with Agricultural and Processed Food Products Export Development Authority." },
                    { code: "PHYTO", name: "Phytosanitary", desc: "Per-shipment certificate issued by Plant Quarantine, Govt. of India." },
                    { code: "EU MRL", name: "Pesticide Compliance", desc: "Maximum Residue Limit testing per consignment via accredited labs." },
                  ].map((c) => (
                    <div key={c.code} className="rounded-2xl bg-white border border-stone-900/10 p-6 text-center">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-linear-to-br from-emerald-100 to-teal-100 border border-emerald-600/15 font-display font-semibold text-emerald-800 text-sm mb-4">
                        {c.code}
                      </div>
                      <div className="font-display text-base font-medium text-stone-900">{c.name}</div>
                      <p className="text-xs text-stone-500 mt-2 leading-relaxed">{c.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── EDITORIAL BANNER — image with overlay text ── */}
      <section className="relative w-full overflow-hidden">
        <div className="relative h-[42vh] md:h-[52vh] min-h-90 w-full">
          <ImageTile src={cat.hero || heroImages.harvestHands} alt="" emoji={item.emoji}
            className="absolute inset-0"
            imgClassName="scale-105"
            overlayClassName="bg-gradient-to-r from-stone-950/85 via-stone-950/55 to-stone-950/20" />
          <div className="relative h-full w-full px-4 sm:px-6 lg:px-10 max-w-400 mx-auto flex items-center">
            <div className="max-w-2xl">
              <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-400 font-medium mb-3">Where it ships</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold text-stone-500 leading-[1.05]">
                {item.name} on global shelves, <span className="text-emerald-400">in 25+ countries.</span>
              </h2>
              <p className="mt-5 text-stone-400 leading-relaxed text-base md:text-lg max-w-xl">
                Active export programmes with retail chains, wholesalers and food manufacturers across the Middle East, Europe, Southeast Asia and Africa.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── EXPORT MARKETS — flat list grouped by region ── */}
      <section className="relative py-16 md:py-20 bg-white/40">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-400 mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10">
            {[
              { region: "Middle East", flag: "🇦🇪", markets: ["UAE", "Saudi Arabia", "Qatar", "Oman", "Kuwait", "Bahrain"] },
              { region: "Southeast Asia", flag: "🇲🇾", markets: ["Malaysia", "Singapore", "Indonesia", "Vietnam", "Thailand"] },
              { region: "Europe", flag: "🇪🇺", markets: ["Netherlands", "United Kingdom", "Germany", "France", "Italy", "Spain"] },
              { region: "Africa", flag: "🇿🇦", markets: ["South Africa", "Kenya", "Mauritius", "Egypt"] },
              { region: "East Asia", flag: "🇯🇵", markets: ["Japan", "South Korea", "Hong Kong"] },
              { region: "Americas", flag: "🇺🇸", markets: ["USA", "Canada (select programmes)"] },
            ].map((m) => (
              <div key={m.region} className="border-l-2 border-emerald-600/30 pl-5">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl text-stone-500">{m.flag}</span>
                  <div className="font-display text-lg font-medium text-stone-900">{m.region}</div>
                </div>
                <ul className="space-y-1.5">
                  {m.markets.map((c) => (
                    <li key={c} className="text-sm text-stone-600 flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full bg-stone-300" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── RELATED PRODUCTS ── */}
      {related.length > 0 && (
        <section className="relative py-16 md:py-20">
          <div className="w-full px-4 sm:px-6 lg:px-10 max-w-400 mx-auto">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 font-medium mb-2">More from {cat.label}</div>
                <h2 className="font-display text-3xl md:text-4xl font-semibold text-stone-900">
                  You may also <span className="text-gradient">consider.</span>
                </h2>
              </div>
              <Link to={`/products/${cat.key}`}
                className="inline-flex items-center gap-2 self-start md:self-auto px-5 py-3 rounded-xl text-sm border border-stone-900/12 hover:border-emerald-700 hover:text-emerald-700 text-stone-800 transition">
                View all {cat.label}
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {related.map((r, i) => (
                <ProductCard
                  key={r.slug}
                  item={r}
                  accent={cat.accent}
                  categoryKey={cat.key}
                  delay={i * 0.05}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── BOTTOM CTA ── */}
      <section className="relative py-14 bg-stone-900">
        <div className="w-full px-4 sm:px-6 lg:px-10 max-w-400 mx-auto">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-300 font-medium mb-2">Ready to source</div>
              <h3 className="font-display text-3xl md:text-4xl font-semibold text-white leading-tight">
                Get a quote for {item.name.toLowerCase()} <span className="text-emerald-300">today.</span>
              </h3>
              <p className="mt-3 text-stone-400 max-w-md">Tell us your destination, target volume and timeline — we'll come back within one business day with a tailored offer.</p>
            </div>
            <div className="md:text-right flex md:justify-end gap-3 flex-wrap">
              <button onClick={openModal}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-sm font-medium text-stone-900 bg-emerald-300 hover:bg-emerald-200 transition">
                Submit Inquiry
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </button>
              <a href="tel:+919876543210"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl text-sm font-medium text-white border border-white/20 hover:bg-white/5 transition">
                Call us
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
